import type { Scale, ScaleType } from '@tunasong/schemas'
import { NoteLib } from '../note/index.js'
import { ScaleLib } from '../scale/index.js'
import { MUTED_STRING } from './chord-shapes.js'
import { addFingerInfo, getFingeringsForScale } from './fingering.js'
import type { GuitarPattern, GuitarPatternGroup, GuitarPatternSpecGroup } from './scale-patterns.js'

/** Five pattern system - e.g., https://www.youtube.com/watch?app=desktop&v=RqpqTGxf9Eo */

const fivePatternSystem: GuitarPatternSpecGroup = {
  id: 'five-pattern-system',
  name: 'Five Pattern System',
  description: 'Five pattern system based on the major scale. Also called the CAGED system.',
  root: 'C',
  type: 'major',
  shapes: [
    {
      name: 'C shape',
      description: 'C shape, CAGED',
      startFret: 0,
      endFret: 4,
      offset: {
        lydian: -1,
      },
    },
    {
      name: 'A shape',
      description: 'A shape, CAGED',
      startFret: 2,
      endFret: 6,
      offset: {
        locrian: 1,
        phrygian: 1,
      },
    },

    {
      name: 'G shape',
      description: 'G shape, CAGED',
      startFret: 4,
      endFret: 8,
      offset: {
        phrygian: 1,
        locrian: 1,
        dorian: 1,
        harmonicminor: 1,
      },
    },
    {
      name: 'E shape',
      description: 'E shape, CAGED',
      startFret: 7,
      endFret: 11,
      offset: {
        lydian: -1,
        locrian: 1,
      },
    },
    {
      name: 'D shape',
      description: 'D shape, CAGED',
      offset: {
        phrygian: 1,
        locrian: 1,
      },
      startFret: 9,
      endFret: 13,
    },
  ],
}

export const mapToScale = (group: GuitarPatternSpecGroup, toScaleType: ScaleType) => {
  // Map to determine if we need to transpose the scale. This is solely for the shapes'
  // position on the fretboard.
  const scaleMap: Partial<Record<ScaleType, ScaleType>> = {
    major: 'major',
    minor: 'minor',
    majorpentatonic: 'major',
    minorpentatonic: 'minor',
    harmonicminor: 'minor',
    aeolian: 'minor',
  }

  const toScaleQuality = scaleMap[toScaleType]

  if (!toScaleQuality || group.type === toScaleQuality) {
    return group
  }

  const { root, type } = group
  // Get the relative major/minor scale (e.g., C major -> A minor).
  // However, for the patterns we need to keep the original root
  const scale = ScaleLib.getRelative({ root, type })
  // Find the chromatic distance between the root and the scale. Ensure it's positive
  const chromaticDistance = (NoteLib.getNoteChromaticDistance(scale.root, root) + 12) % 12

  return {
    ...group,
    root,
    type: scale.type,
    shapes: group.shapes.map(shape => {
      const startFret = shape.startFret + chromaticDistance
      const endFret = shape.endFret + chromaticDistance
      const wrap = startFret >= 12
      return {
        ...shape,
        startFret: wrap ? startFret - 12 : startFret,
        endFret: wrap ? endFret - 12 : endFret,
      }
    }),
  }
}

export const generateScalePatternGroup = ({
  group: providedGroup,
  rootScale,
  startFret = 0,
  endFret = 30,
  allowTruncateSteps = 0,
  allowOpenStrings = false,
  // Wrap around if all the fingerings are above the 12th fret
  wrapAround = true,
}: {
  group: GuitarPatternSpecGroup
  rootScale: Scale
  startFret?: number
  endFret?: number
  // Allow truncating the pattern if it transposes to "negative" frets
  allowTruncateSteps?: number
  // Allow a shape to have open strings
  allowOpenStrings?: boolean
  wrapAround?: boolean
}): GuitarPatternGroup => {
  const group = mapToScale(providedGroup, rootScale.type ?? 'major')

  const chromaticDistance = NoteLib.getNoteChromaticDistance(group.root, rootScale.root)

  const allFingerings = getFingeringsForScale({ scale: rootScale, startFret, endFret })

  // Now we fit all the notes inside the fret range to actual fingerings
  const shapes = group.shapes.map(shape => {
    const offset = shape.offset?.[rootScale.type ?? 'major'] ?? 0
    let newStartFret = shape.startFret + chromaticDistance + offset
    let newEndFret = shape.endFret + chromaticDistance + offset

    // The minium fret number is 0 (open string) if allowOpenStrings is true, otherwise 1
    const minFret = allowOpenStrings ? 0 : 1

    const allowTruncate = Math.abs(allowTruncateSteps)
    const wrap = wrapAround && newStartFret + allowTruncate >= 12 + minFret
    if (wrap) {
      newStartFret = Math.max(newStartFret - 12, minFret)
      newEndFret = Math.max(newEndFret - 12, minFret)
    }

    if (newStartFret < minFret) {
      newStartFret = newStartFret + 12
      newEndFret = newEndFret + 12
    }

    // Filter the fingerings between startFret and endFret
    const patternFingerings = allFingerings.filter(
      fingering => fingering.fret !== MUTED_STRING && fingering.fret >= newStartFret && fingering.fret <= newEndFret
    )

    const fingerings = addFingerInfo(patternFingerings)

    return {
      name: shape.name,
      description: shape.description,
      fingerings,
    } satisfies GuitarPattern
  })

  return {
    ...group,
    scale: rootScale,
    shapes,
  }
}

export const scalePatterns = { fivePatternSystem, CAGED: fivePatternSystem }
